import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, UserPlus, BadgeCheck, Cake } from "lucide-react";
import { toast } from "sonner";

export default function Customers() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", dob: "", phone: "", email: "", id_number: "", loyalty: false });

  const load = useCallback(() => api.get("/customers", { params: { q: q || undefined } }).then((r) => setList(r.data)).catch(() => {}), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openDetail = (id) => api.get(`/customers/${id}`).then((r) => setDetail(r.data)).catch(() => {});
  const create = () => api.post("/customers", form).then(() => { toast.success("Customer created"); setCreating(false); setForm({ name: "", dob: "", phone: "", email: "", id_number: "", loyalty: false }); load(); }).catch((e) => toast.error(formatApiError(e)));

  return (
    <div className="max-w-5xl space-y-4">
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="pl-9" data-testid="customers-search-input" /></div>
        <Button onClick={() => setCreating(true)} className="bg-indigo-600" data-testid="new-customer-btn"><UserPlus className="w-4 h-4 mr-1.5" />New customer</Button>
      </div>
      <Card className="divide-y">
        {list.map((c) => (
          <button key={c.id} onClick={() => openDetail(c.id)} className="w-full text-left flex items-center gap-3 p-3" data-testid={`customer-row-${c.id}`}>
            <div className="flex-1"><span className="font-medium text-slate-800">{c.name}</span>{c.loyalty && <Badge className="ml-2 bg-amber-100 text-amber-700">Loyalty</Badge>}<div className="text-xs text-slate-400">{c.phone} · {c.visits || 0} visits</div></div>
            <Badge className={(c.age || 0) >= 21 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}><BadgeCheck className="w-3 h-3 mr-0.5" />{(c.age || 0) >= 21 ? "21+" : c.age}</Badge>
            <span className="text-sm font-semibold">{money(c.lifetime_spend)}</span>
          </button>
        ))}
        {list.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No customers.</div>}
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-2">
                <Badge className={(detail.age || 0) >= 21 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{(detail.age || 0) >= 21 ? "21+ verified" : `Age ${detail.age}`}</Badge>
                {detail.loyalty && <Badge className="bg-amber-100 text-amber-700">Loyalty</Badge>}
                {detail.birthday && <Badge variant="secondary"><Cake className="w-3 h-3 mr-1" />{detail.birthday}</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-slate-600">
                <div>Lifetime spend<div className="font-semibold text-slate-900">{money(detail.lifetime_spend)}</div></div>
                <div>Visits<div className="font-semibold text-slate-900">{detail.visits || 0}</div></div>
                <div>Phone<div className="text-slate-800">{detail.phone || "—"}</div></div>
                <div>Last visit<div className="text-slate-800">{fmtDate(detail.last_visit)}</div></div>
              </div>
              <div className="border-t pt-2"><div className="text-xs text-slate-500 mb-1">Recent visits</div>
                {(detail.visits_history || []).slice(0, 8).map((s) => <div key={s.id} className="flex justify-between text-xs py-0.5"><span>{fmtDate(s.created_at)}</span><span>{money(s.total)}</span></div>)}
                {(detail.visits_history || []).length === 0 && <div className="text-xs text-slate-400">No visits.</div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New customer</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            {[["name", "Full name"], ["dob", "DOB (YYYY-MM-DD)"], ["phone", "Phone"], ["email", "Email"], ["id_number", "ID number"]].map(([k, lbl]) => (
              <div key={k}><Label className="text-xs">{lbl}</Label><Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`cust-${k}`} /></div>
            ))}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.loyalty} onChange={(e) => setForm({ ...form, loyalty: e.target.checked })} /> Loyalty member</label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button><Button onClick={create} data-testid="save-new-customer-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
